import { observable, action } from 'mobx'
import Auth, { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { IUserCredentials } from '~/types'
import { cognitoConfig } from '~/config'
import { analyze } from './analytics'

export class AuthStore {
  @observable
  public loading: boolean = false

  @observable
  public error?: Error = null

  constructor() {
    Auth.configure(cognitoConfig)
  }

  /** AuthStore.handleError is used to handle errors in other AuthStore methods */
  @action
  private handleError = (error: Error) => {
    console.error(error)

    this.error = error

    throw error
  }

  /** AuthStore.loginWithCredentials is used to sign user in based on email and password  */
  @action
  @analyze('auth', 'sign-in-email')
  public loginWithCredentials(credentials: IUserCredentials) {
    this.loading = true
    this.error = null

    return Auth.signIn(credentials.email, credentials.password)
      .catch(this.handleError)
      .finally(() => {
        this.loading = false
      })
  }

  /** AuthStore.registerWithCredentials is used to register user based on email and password  */
  @action
  @analyze('auth', 'sign-up-email')
  public registerWithCredentials(credentials: IUserCredentials) {
    this.loading = true
    this.error = null

    return Auth.signUp({
      username: credentials.email,
      password: credentials.password,
    })
      .catch(this.handleError)
      .finally(() => {
        this.loading = false
      })
  }

  /** AuthStore.loginWithGoogle is used to authenticate user with Google identity provider  */
  @action
  @analyze('auth', 'sign-in-google')
  public loginWithGoogle() {
    this.loading = true
    this.error = null

    return Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google,
    })
      .catch(this.handleError)
      .finally(() => {
        this.loading = false
      })
  }

  /** AuthStore.loginWithFacebook is used to authenticate user with Facebook identity provider  */
  @action
  @analyze('auth', 'sign-in-facebook')
  public loginWithFacebook() {
    this.loading = true
    this.error = null

    return Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Facebook,
    })
      .catch(this.handleError)
      .finally(() => {
        this.loading = false
      })
  }
}
